import React from 'react';

import type { ElementChipDescriptor } from '../elementChipsTypes';
import { useChipSelectionState } from './useChipSelectionState';
import { ChipSelectionClickHandler, useChipSelectionInteractions } from './useChipSelectionInteractions';

export type SelectableChipRenderProps = {
    isSelected: boolean;
    canActivate: boolean;
    isEditing: boolean;
    setEditing: (isEditing: boolean) => void;
    deselect: () => void;
    chipRef: React.RefObject<HTMLSpanElement>;
    chipProps: React.HTMLAttributes<HTMLSpanElement> & {
        role: React.AriaRole;
        tabIndex: number;
        className: string;
    };
};

export type SelectableChipProps = {
    chip: ElementChipDescriptor;
    canModify: boolean;
    /** Deletes the chip's data on Delete/Backspace while selected. */
    onDelete?: () => void;
    children: (renderProps: SelectableChipRenderProps) => React.ReactNode;
};

/**
 * Behaviour wrapper for chips with a selection state (e.g. labels, assignments).
 * Clicking selects the chip; repeat clicks are a no-op — the chip's data is only
 * ever changed from within its selection-owned popup or inline editor.
 */
const SelectableChip: React.FC<SelectableChipProps> = ({ chip, canModify, onDelete, children }) => {
    const { isSelected, canSelect, isEditing, select, deselect, setEditing } = useChipSelectionState({
        chip,
        canModify,
    });

    const handleClick: ChipSelectionClickHandler = (event, focusChip) => {
        if (isSelected) return;
        if (!canSelect) return;

        select();
        focusChip();
    };

    const { chipRef, chipProps } = useChipSelectionInteractions({
        isSelected,
        canActivate: canSelect,
        onClick: handleClick,
        onDeselect: deselect,
        onDelete: canModify ? onDelete : undefined,
        onEditingToggle: () => setEditing(!isEditing),
    });

    return <>{children({ isSelected, canActivate: canSelect, isEditing, setEditing, deselect, chipRef, chipProps })}</>;
};

export default SelectableChip;
